import { Link } from 'react-router-dom';
import { formatRelativeDate } from '@/utils/date';
import { downloadRenderOutputFile } from '@/services/renders';
import type { RenderJobSummary } from '@/types/api';

function formatDurationMs(ms: number | null): string {
  if (ms == null || ms < 0) return '—';
  if (ms < 1000) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)} s`;
  const m = Math.floor(s / 60);
  const r = Math.round(s % 60);
  return `${m}m ${r}s`;
}

function statusClass(status: RenderJobSummary['status']): string {
  switch (status) {
    case 'completed':
      return 'text-success font-bold';
    case 'failed':
      return 'text-error font-bold';
    case 'rendering':
      return 'text-primary font-bold';
    default:
      return 'text-on-surface-variant';
  }
}

export interface RenderJobListProps {
  rows: RenderJobSummary[];
  /** When true, show a Creative column linking to the editor. */
  showCreativeColumn?: boolean;
  emptyMessage?: string;
}

export function RenderJobList({
  rows,
  showCreativeColumn = false,
  emptyMessage = 'No renders yet.',
}: RenderJobListProps) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center bg-surface-container-lowest rounded-2xl border border-outline-variant/20">
        <span className="material-symbols-outlined text-4xl text-on-surface-variant/30 mb-3">
          movie
        </span>
        <p className="text-sm font-medium text-on-surface-variant">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto border border-outline-variant/20 rounded-2xl bg-surface-container-lowest">
      <table className="w-full text-left text-sm whitespace-nowrap">
        <thead>
          <tr className="border-b border-outline-variant/10 text-on-surface-variant text-xs uppercase tracking-wider bg-surface-container-low/30">
            {showCreativeColumn && <th className="px-5 py-3 font-bold">Creative</th>}
            <th className="px-5 py-3 font-bold">Status</th>
            <th className="px-5 py-3 font-bold">Resolution</th>
            <th className="px-5 py-3 font-bold">Completed</th>
            <th className="px-5 py-3 font-bold">Render Time</th>
            <th className="px-5 py-3 font-bold text-right border-l border-outline-variant/5">
              Output
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="border-b border-outline-variant/10 last:border-0 hover:bg-surface-container-low/50 transition-colors"
            >
              {showCreativeColumn && (
                <td className="px-5 py-3">
                  <Link
                    to={`/creatives/${r.creative_id}`}
                    className="text-primary hover:text-primary-dim hover:underline font-bold transition-colors"
                  >
                    {r.creative_name}
                  </Link>
                </td>
              )}
              <td className={`px-5 py-3 uppercase tracking-wide text-xs ${statusClass(r.status)}`}>
                {r.status}
              </td>
              <td className="px-5 py-3 text-on-surface-variant font-medium">
                {r.output_resolution}
              </td>
              <td className="px-5 py-3 text-on-surface-variant">
                {r.completed_at ? formatRelativeDate(r.completed_at) : '—'}
              </td>
              <td className="px-5 py-3 text-on-surface-variant">
                {formatDurationMs(r.render_duration_ms)}
              </td>
              <td className="px-5 py-3 text-right border-l border-outline-variant/5">
                {r.status === 'completed' && r.output_uri ? (
                  <button
                    type="button"
                    onClick={() =>
                      void downloadRenderOutputFile(
                        r.creative_id,
                        r.id,
                        `${r.creative_name}-render-${r.id}.mp4`,
                      )
                    }
                    className="text-primary hover:text-primary-dim text-xs font-bold uppercase tracking-wider cursor-pointer bg-primary/5 hover:bg-primary/10 px-3 py-1.5 rounded-lg transition-colors inline-flex items-center gap-1.5"
                  >
                    <span className="material-symbols-outlined text-[16px]">download</span>
                    Download
                  </button>
                ) : (
                  <span className="text-on-surface-variant/50 text-xs">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
